import * as React from 'react';
import { Avatar } from '@mui/material';
import { User as UserIcon } from '@phosphor-icons/react';

import type { User } from '@/types/v2/user';
import { getS3PresignedUrlFromPath } from '@/utils/s3';

interface UserAvatarProps {
  user?: User | null;
  size?: string;
  sx?: object;
  onClick?: () => void;
}

export function UserAvatar({ user, size = '40px', sx = {}, onClick }: UserAvatarProps): React.JSX.Element {
  const [avatarUrl, setAvatarUrl] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);

  React.useEffect(() => {
    const fetchAvatarUrl = async () => {
      if (!user?.avatar?.path) {
        setAvatarUrl(null);
        return;
      }

      try {
        setIsLoading(true);
        setHasError(false);
        const url = await getS3PresignedUrlFromPath(user.avatar.path);
        setAvatarUrl(url);
      } catch (error) {
        console.error('Failed to get presigned URL:', error);
        setHasError(true);
        // Fallback to direct path if presigned URL fails
        setAvatarUrl(user.avatar.path);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAvatarUrl();
  }, [user?.avatar?.path]);

  const handleImageError = () => {
    console.log('Avatar image failed to load, falling back to icon');
    setHasError(true);
    setAvatarUrl(null);
  };

  const displayAvatarUrl = !hasError && avatarUrl ? avatarUrl : undefined;

  return (
    <Avatar
      onClick={onClick}
      src={displayAvatarUrl}
      sx={{
        '--Avatar-size': size,
        cursor: onClick ? 'pointer' : 'default',
        ...sx,
      }}
      onError={handleImageError}
    >
      {!displayAvatarUrl && <UserIcon fontSize="var(--icon-fontSize-md)" />}
    </Avatar>
  );
}
